import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { WorkspaceContextService } from '../services/workspace-context.service';

/**
 * Adds `did` (the owner's `delegatorId`) while a delegate session is active:
 * - consumer / provider / org-admin / platform-admin panel pages
 * - catalogue pages (landing, datasets, models, usecases) — every API on those pages
 * - user-interaction APIs (likes, bookmarks, feedback) from any page
 *
 * Delegated Access and the grants-received list go out without `did`.
 * The user API skips `did` only on the profile page; Manage Users pages still send it.
 * Creating an access request never carries `did`; dataset/model Request Access
 * stays hidden in the UI for delegates.
 */
export const delegationIdInterceptor: HttpInterceptorFn = (req, next) => {
  const pageUrl = inject(Router).url;
  if (shouldSkipDelegationHeader(req.url, pageUrl) || req.headers.has('did')) {
    return next(req);
  }

  const workspace = inject(WorkspaceContextService);
  const did = workspace.getActiveDelegatorId();
  if (
    !did ||
    !workspace.isDelegateSession() ||
    !shouldAttachDelegationHeader(pageUrl, req.url)
  ) {
    return next(req);
  }

  return next(
    req.clone({
      setHeaders: { did },
    }),
  );
};

function requestPath(url: string): string {
  return (url || '').split('?')[0];
}

/** True when this request should carry `did` (assuming a delegate session). */
export function shouldAttachDelegationHeader(
  pageUrl: string,
  reqUrl: string,
): boolean {
  if (isDelegatedAccessPage(pageUrl)) {
    return false;
  }
  return (
    isDelegatePanelPage(pageUrl) ||
    isCataloguePage(pageUrl) ||
    isInteractionApiRequest(reqUrl)
  );
}

export function isDelegatePanelPage(url: string): boolean {
  return /^\/(consumer|provider|org-admin|admin)(\/|$)/.test(requestPath(url));
}

export function isDelegatedAccessPage(url: string): boolean {
  return requestPath(url).includes('/delegated-access');
}

/** Public catalogue browsing: landing, dataset/model/usecase lists and details. */
export function isCataloguePage(url: string): boolean {
  const path = requestPath(url) || '/';
  if (path === '/') {
    return true;
  }
  return /^\/(datasets?|models?|usecases?)(\/|$)/.test(path);
}

/**
 * User-interaction APIs (`/user/interactions`, `/user/feedback`, likes, bookmarks).
 * Does not match `/auth/user`.
 */
export function isInteractionApiRequest(url: string): boolean {
  const path = requestPath(url);
  return (
    /\/user\/(interactions|feedback|bookmark|like)(\/|$)/.test(path) ||
    /\/user(bookmark|like)$/.test(path)
  );
}

/** POST …/access_request (create). `…/access_request/has_access` is not this. */
export function isCreateAccessRequestApi(url: string): boolean {
  return /\/access_request\/?$/.test(requestPath(url));
}

/** GET /delegation/delegate is "who delegated to me", so it must stay the logged-in user. */
export function isOwnDelegationListRequest(url: string): boolean {
  return /\/delegation\/delegate\/?$/.test(requestPath(url));
}

/** User API (`/auth/user/...`), not the users list (`/auth/users`). */
export function isUserApiRequest(url: string): boolean {
  return /\/auth\/user(\/|$)/.test(requestPath(url));
}

/** Profile loads the logged-in user. Other panel pages, including Manage Users, keep `did`. */
export function isProfilePage(url: string): boolean {
  return /^\/(consumer|provider|org-admin|admin)\/profile\/?$/.test(
    requestPath(url),
  );
}

function shouldSkipDelegationHeader(url: string, pageUrl: string): boolean {
  return (
    isOwnDelegationListRequest(url) ||
    isCreateAccessRequestApi(url) ||
    (isUserApiRequest(url) && isProfilePage(pageUrl)) ||
    url.includes('keycloak') ||
    url.includes('/auth/realms/') ||
    url.includes('/protocol/openid-connect/revoke') ||
    url.includes('.s3.') ||
    url.includes('.amazonaws.com') ||
    url.includes('minio.iudx.io') ||
    url.includes('storage.googleapis.com')
  );
}
